import { useAuth } from '@/components/auth/authProvider';
import usePermissions from '@/hooks/usePermissions';
import { IconSize as DsmIconSize, PalmettoIcon } from '@palmetto/dsm-components';
import {
  Box,
  Button,
  Card,
  Icon,
  IconName,
  Modal,
  Spinner,
  TextInput,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  EXTENDED_WARRANTY_PROGRAM_TYPES,
  OrgAccountBundlingResolution,
  OrganizationStatus,
  OrganizationStatusLabels,
  OrganizationTypeLabels,
  ProgramType,
  ProgramTypeLabels,
  UserPermissions,
} from 'types';

import { OrganizationNode } from '../../../types/Organizations';
import RequirePalmettoFinanceUser, { isPalmettoFinanceUser } from '../../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../../auth/requirePermissions';
import { PropertyValue } from '../../PropertyValue';

import { OrganizationAuthorizedSigners } from './OrganizationAuthorizedSigners';
import { OrganizationExtendedWarranties } from './OrganizationExtendedWarranties';

interface ViewPropertiesProps {
  organization: OrganizationNode | undefined;
}

const palmettoComponentsIconMap: Partial<Record<ProgramType, IconName>> = {
  hvac: 'heat-pump',
  solar: 'sun',
  doePr: 'lightbulb', // TODO: replace with icon when we have it
  newCommunity: 'sun', // TODO: replace with icon when we have it
  newHomes: 'sun', // TODO: replace with icon when we have it
  energyBackup: 'electricity-bolt',
  // [EDUCATED GUESS] Reuse electricity-bolt icon for generator until a dedicated icon exists.
  generator: 'electricity-bolt',
  geothermal: 'heat-pump', // Geothermal uses heat-pump icon like HVAC
};

const ProgramTypeIcon = ({ programType, size }: { programType: ProgramType; size: DsmIconSize }) => {
  const palmettoComponentsIcon = palmettoComponentsIconMap[programType];
  if (palmettoComponentsIcon) {
    return <Icon name={palmettoComponentsIcon} size={size} />;
  }
  if (programType === ProgramType.waterHeater) {
    return <PalmettoIcon name="waterHeater" size={size} />;
  }
  return null;
};

const VisibleOrganizationsProperty = ({ aliases }: { aliases: string[] }) => {
  const { isOpen, handleOpen, handleClose } = useOpenClose();
  const [search, setSearch] = useState('');

  const uniqueAliases = useMemo(() => [...new Set(aliases)], [aliases]);

  const filtered = useMemo(
    () => uniqueAliases.filter((alias) => alias.toLowerCase().includes(search.toLowerCase())),
    [uniqueAliases, search],
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value), []);

  const handleCloseAndClear = useCallback(() => {
    setSearch('');
    handleClose();
  }, [handleClose]);

  if (uniqueAliases.length <= 20) {
    return <PropertyValue label="Visible Organizations" value={uniqueAliases.join(', ')} />;
  }

  return (
    <>
      <PropertyValue
        label="Visible Organizations"
        value={
          <Box direction="row" gap="sm" alignItems="center">
            <span>{uniqueAliases.length} organizations</span>
            <Button variant="tertiary" tone="neutral" size="sm" onClick={handleOpen}>
              View list
            </Button>
          </Box>
        }
      />
      <Modal
        isOpen={isOpen}
        onDismiss={handleCloseAndClear}
        maxWidth="4xl"
        ariaLabelledBy="visibleOrgsHeader"
        fullScreenMobile
      >
        <Modal.Header
          id="visibleOrgsHeader"
          title={`Visible Organizations (${uniqueAliases.length})`}
          onDismiss={handleCloseAndClear}
        />
        <Modal.Body background="secondary" style={{ height: '60vh' }}>
          <Box display="flex" direction="column" height="100" childGap="md">
            <TextInput
              id="visibleOrgsSearch"
              label="Search organizations"
              hideLabel
              placeholder="Search organizations..."
              value={search}
              onChange={handleSearchChange}
            />
            <Box flex="auto" overflow="auto">
              {filtered.map((alias) => (
                <Box key={alias} padding="xs sm" borderWidth="0 0 xs 0" borderColor="separator" fontSize="sm">
                  {alias}
                </Box>
              ))}
              {filtered.length === 0 && (
                <Box padding="md" color="grey-400" fontSize="sm">
                  No organizations match your search.
                </Box>
              )}
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={handleCloseAndClear}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const AccountBundlingProperty = ({ accountBundling }: { accountBundling?: OrgAccountBundlingResolution }) => {
  if (!accountBundling?.enabled) {
    return <PropertyValue label="Account Bundling" value="Disabled" />;
  }

  if (accountBundling.isInherited && accountBundling.source) {
    return (
      <PropertyValue
        label="Account Bundling"
        value={
          <span>
            Enabled (inherited from{' '}
            <Link to={`/settings/organizations/${accountBundling.source.alias}`}>{accountBundling.source.name}</Link>
            )
          </span>
        }
      />
    );
  }

  return <PropertyValue label="Account Bundling" value="Enabled" />;
};

const ViewProperties = ({ organization }: ViewPropertiesProps) => {
  const { claims } = useAuth();
  const userPermissions = usePermissions();
  const isLightReachAdmin = isPalmettoFinanceUser(claims) && userPermissions.includes(UserPermissions.admin);
  const { salesforceOrgInfo = false, accountBundling = false } = useFlags();
  const organizationStatusLabel =
    OrganizationStatusLabels[organization?.status as keyof typeof OrganizationStatusLabels];
  const organizationStatusBadgeColor = (() => {
    switch (organization?.status) {
      case OrganizationStatus.active:
        return 'success';
      case OrganizationStatus.noNewSales:
        return 'warning';
      case OrganizationStatus.offBoarded:
        return 'danger';
      case OrganizationStatus.readOnly:
        return 'danger';
      default:
        return undefined;
    }
  })();
  return (
    <Box padding="0 lg lg lg" gap="xs">
      <PropertyValue label="Name" value={organization?.name || ''} />
      <PropertyValue
        label="Status"
        isBadgeValue
        variantBadgeValue={organizationStatusBadgeColor}
        value={organizationStatusLabel || ''}
      />
      {salesforceOrgInfo && (
        <>
          <PropertyValue label="Relationship Manager" value={organization?.salesforceData?.lrAccountRepName || ''} />

          <PropertyValue label="Tier" value={organization?.salesforceData?.lrTier || ''} />
        </>
      )}
      <PropertyValue label="Alias" value={organization?.alias || ''} />
      <PropertyValue
        label="Parent Organization Alias"
        value={
          organization?.parentAlias ? (
            isLightReachAdmin ? (
              <Link to={`${location.origin}/settings/organizations/${organization.parentAlias}`}>
                {organization.parentAlias}
              </Link>
            ) : (
              organization.parentAlias
            )
          ) : (
            'None'
          )
        }
      />
      {!!organization?.pricingTierId && (
        <RequirePalmettoFinanceUser>
          <RequirePermissions permissions={[UserPermissions.admin]}>
            <PropertyValue label="Pricing Tier" value={organization?.pricingTierId || ''} />
          </RequirePermissions>
        </RequirePalmettoFinanceUser>
      )}
      <VisibleOrganizationsProperty aliases={organization?.visibleOrganizationAliases ?? []} />
      <PropertyValue label="Insurance Company Name" value={organization?.insuranceCompanyName || ''} />

      <PropertyValue
        label="Insurance Company Phone Number"
        value={organization?.insuranceCompanyPhoneNumber || ''}
      />

      <PropertyValue label="Company Phone Number" value={organization?.phoneNumber || ''} />
      <PropertyValue label="Company Email" value={organization?.email || ''} />
      <PropertyValue label="Federal Employer Id" value={organization?.federalEmployerId || ''} />
      <PropertyValue label="Address" value={organization?.address || ''} />
      <PropertyValue
        label="City, State Zip"
        value={
          organization?.city && organization?.state && organization?.zip
            ? `${organization.city}, ${organization.state} ${organization.zip}`
            : ''
        }
      />
      {isLightReachAdmin && accountBundling && (
        <AccountBundlingProperty accountBundling={organization?.accountBundling} />
      )}

      <Box margin="lg 0 0 0" childGap="md">
        <Box as="h4" fontSize="md" fontWeight="medium">
          Program Types
        </Box>
        <Box direction="row" childGap="xl" alignItems="center">
          {organization?.programTypes?.map((programType) => (
            <Box key={programType} direction="row" gap="sm" color="grey-400" alignItems="center">
              <ProgramTypeIcon programType={programType} size="2xl" />
              <Box>{ProgramTypeLabels[programType]}</Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Box margin="lg 0 0 0" childGap="md">
        <Box as="h4" fontSize="md" fontWeight="medium">
          Organization Types
        </Box>
        <Box direction="row" childGap="xl" alignItems="center">
          {organization?.organizationTypes?.map((organizationType) => (
            <Box key={organizationType} direction="row" childGap="sm" color="grey-400" alignItems="center">
              <Box>{OrganizationTypeLabels[organizationType]}</Box>
            </Box>
          ))}
        </Box>
      </Box>
      <OrganizationAuthorizedSigners organization={organization} />
      {organization?.programTypes?.some((programType) => EXTENDED_WARRANTY_PROGRAM_TYPES.has(programType)) && (
        <OrganizationExtendedWarranties organization={organization} />
      )}
    </Box>
  );
};

interface OrganizationPropertiesProps {
  organization: OrganizationNode | undefined;
  isLoading?: boolean;
  className?: string;
}

const OrganizationProperties = ({ className, organization, isLoading }: OrganizationPropertiesProps) => {
  const navigate = useNavigate();
  const { isPhone } = useBreakpoint();

  return (
    <Card className={className}>
      <Box childGap="lg" padding="lg" alignItems="center" direction="row" justifyContent="space-between">
        <Box as="h3" fontWeight="medium" fontSize="md">
          Details
        </Box>
        <RequirePermissions permissions={[UserPermissions.admin]}>
          {organization && (
            <Button
              as="a"
              navigate={() => navigate(`/settings/organizations/${organization.alias}/edit`)}
              variant="primary"
              tone="neutral"
              size="sm"
              aria-label="Edit Organization"
              iconPrefix="pencil"
            >
              {!isPhone && 'Edit'}
            </Button>
          )}
        </RequirePermissions>
      </Box>
      {isLoading && (
        <Box padding="0 lg lg lg">
          <Spinner />
        </Box>
      )}
      {!isLoading && !organization && <Box padding="0 lg lg lg">Organization not found</Box>}
      {!isLoading && organization && <ViewProperties organization={organization} />}
    </Card>
  );
};

export default OrganizationProperties;
